import { useEffect, useState } from 'react';
import { AppDispatch } from '../core/data/redux/store'; // Assuming you're using this
import { useDispatch, useSelector } from 'react-redux';
import { createEducation, updateEducation, updateParent } from '../core/data/redux/employee/employeeAction'; // Import your action
import { message } from 'antd';

interface EducationFormValues {
  highestEducation: string;
  fieldOfStudy: string;
  institutionName: string;
  graduationYear: number | string;
  address: string;
  gpa: string;
  startDate: Date | null | string;
  endDate: Date | null | string;
  shortNote: string;
}

export const useQualificationForm = () => {
  const [educationData, setEducationData] = useState<EducationFormValues>({
    highestEducation: '',
    fieldOfStudy: '',
    institutionName: '',
    graduationYear:'',
    address: '',
    gpa: '',
    startDate: '',
    endDate: '',
    shortNote: '',
  });

  const [educationErrors, setEducationErrors] = useState<{ [key: string]: string }>({});
  const { employee } = useSelector((state: any) => state.employee);
  const { user } = useSelector((state: any) => state.auth);
  const { employeeFullDetails } = useSelector((state: any) => state.employee);
  const educationId = employeeFullDetails?.education[0]?.Id;
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (employeeFullDetails && employeeFullDetails.education.length > 0) {
      const education = employeeFullDetails.education[0];
      setEducationData({
        highestEducation: education.HighestEducation || '',
        fieldOfStudy: education.StudyMajor || '',
        institutionName: education.SchoolName || '',
        graduationYear: education.GraduationYear || new Date().getFullYear(),
        address: education.InstituteAddress || '',
        gpa: education.GPA_Percentage || '',
        startDate: education.StartDate ? new Date(education.StartDate) : '',
        endDate: education.StartDate ? new Date(education.EndDate) : '',
        shortNote: education.ShortNote || '',
      });
    }
  }, [employeeFullDetails]);

  const handleEducationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEducationData((prevData) => ({ ...prevData, [name]: value }));
  };

  const validateQualification = async (): Promise<{ isValid: boolean; missingFields?: string[] }> => {
    let tempErrors: { [key: string]: string } = {};
    let isValid = true;
    const missingFields: string[] = [];

    if (!educationData.highestEducation) {
      tempErrors.highestEducation = 'Highest education is required';
      missingFields.push('Highest Education');
      isValid = false;
    }
    if (!educationData.fieldOfStudy) {
      tempErrors.fieldOfStudy = 'Field of study is required';
      missingFields.push('Field of Study');
      isValid = false;
    }
    if (!educationData.institutionName) {
      tempErrors.institutionName = 'Institution name is required';
      missingFields.push('Institution Name');
      isValid = false;
    }
    if (!educationData.graduationYear) {
      tempErrors.graduationYear = 'Graduation year is required';
      missingFields.push('Graduation Year');
      isValid = false;
    }
    if (!educationData.address) {
      tempErrors.address = 'Address is required';
      missingFields.push('Address');
      isValid = false;
    }
    if (!educationData.startDate) {
      tempErrors.startDate = 'Start date is required';
      missingFields.push('Start Date');
      isValid = false;
    }
    if (!educationData.endDate) {
      tempErrors.endDate = 'End date is required';
      missingFields.push('End Date');
      isValid = false;
    }

    if (educationData.startDate && educationData.endDate) {
      const start = new Date(educationData.startDate);
      const end = new Date(educationData.endDate);

      if (start > end) {
        tempErrors.startDate = 'Start date cannot be after end date';
        tempErrors.endDate = 'End date cannot be before start date';
        missingFields.push("Valid Dates");
        isValid = false;
      }
    }

    setEducationErrors(tempErrors);
    console.log(missingFields, 'missing fields')
    return { isValid, missingFields };
  };


  const resetEducationForm = () => {
    setEducationData({
      highestEducation: '',
      fieldOfStudy: '',
      institutionName: '',
      graduationYear: '',
      address: '',
      gpa: '',
      startDate: '',
      endDate: '',
      shortNote: '',
    });
    setEducationErrors({});
  };

  const handleEducationSubmit = async (e: React.FormEvent, resetForm: boolean = false, showMessage: boolean = true): Promise<boolean> => {
    return new Promise(async (resolve) => {
      e.preventDefault();

      if (!educationData.highestEducation || !educationData.fieldOfStudy || !educationData.institutionName || !educationData.graduationYear || !educationData.address || !educationData.startDate || !educationData.endDate) {
        if (showMessage) {
          message.warning("Please fill all fields to add/update Education.");
        }
        resolve(false);
        return;
      }

      const validation = await validateQualification();
      if (!validation.isValid) {
        resolve(false);
        return;
      }

      const educationPayload = {
        HighestEducation: educationData.highestEducation,
        StudyMajor: educationData.fieldOfStudy,
        SchoolName: educationData.institutionName,
        GraduationYear: educationData.graduationYear,
        InstituteAddress: educationData.address,
        GPA_Percentage: educationData.gpa === "" ? null : educationData.gpa,
        ShortNote: educationData.shortNote,
        StartDate: educationData.startDate,
        EndDate: educationData.endDate,
        CreatedBy: user?.UserId,
        ModifiedBy: user?.UserId,
      };

      if (educationId) {
        // Update existing education record
        const result = dispatch(updateEducation({ Id: educationId, educationData: educationPayload }));
        result.then((response: any) => {
          if (response.meta.requestStatus === "fulfilled") {
            if (showMessage) message.success("Education updated successfully");
            if (resetForm) resetEducationForm();
            resolve(true);
          } else {
            message.error("Something went wrong while updating");
            resolve(false);
          }
        }).catch((error: any) => {
          message.error("Failed to update education");
          resolve(false);
        });
      } else {
        // Create new education record
        const employeeIdToUse =
          employeeFullDetails?.employee?.[0]?.EmployeeID || employee?.EmployeeId;

        const result = dispatch(
          createEducation({ employeeId: employeeIdToUse, educationData: educationPayload })
        );

        result.then((response: any) => {
          if (response.meta.requestStatus === "fulfilled") {
            if (showMessage) message.success("Education saved successfully");
            if (resetForm) resetEducationForm();
            resolve(true);
          } else if (response.meta.requestStatus === "rejected") {
            message.error(response.payload);
            resolve(false);
          } else {
            message.error("Something went wrong while saving");
            resolve(false);
          }
        }).catch((error: any) => {
          message.error("Failed to save education");
          resolve(false);
        });
      }
    });
  };


  return {
    educationData,
    handleEducationChange,
    handleEducationSubmit,
    educationErrors,
    setEducationData,
    resetEducationForm,
    validateQualification
  };
};
